/**
 * Pagina Crediti Seller - Dashboard per visualizzare crediti buyer
 * Mostra tutti i buyer con credito attivo, saldi e ordini in sospeso
 */
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '@/lib/utils';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Wallet,
  Users,
  TrendingUp,
  AlertTriangle,
  Search,
  Building2,
  Euro,
  Clock,
  CheckCircle,
  ArrowRight,
  Loader2,
  Receipt,
  RefreshCw,
  Calendar,
  Settings2
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface BuyerCredit {
  id: string;
  buyer_id: string;
  buyer_email?: string;
  buyer_company?: string;
  credit_enabled: boolean;
  credit_type: 'simple' | 'revolving';
  credit_limit: number;
  current_balance: number;
  unpaid_orders: string[];
  billing_cycle?: 'weekly' | 'biweekly' | 'monthly' | 'quarterly';
  billing_day?: number;
  current_cycle_start?: string;
  current_cycle_end?: string;
  alert_threshold_percent?: number;
  block_on_limit?: boolean;
  total_credit_used?: number;
  total_payments_made?: number;
  cycles_completed?: number;
  notes?: string;
}

interface CreditSummary {
  active_credits_count: number;
  total_outstanding: number;
  total_credit_limit: number;
}

const SellerCreditsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [credits, setCredits] = useState<BuyerCredit[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerCredit | null>(null);
  const [buyerOrders, setBuyerOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    if (user?.role === 'seller') {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [summaryRes, creditsRes] = await Promise.all([
        axios.get(`${API}/billing/credits-summary`),
        axios.get(`${API}/billing/buyer-credits`)
      ]);
      
      setSummary(summaryRes.data);
      setCredits(creditsRes.data.buyer_credits || []);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewBuyerDetails = async (credit: BuyerCredit) => {
    setSelectedBuyer(credit);
    setLoadingOrders(true);
    
    try {
      // Recupera gli ordini non pagati per questo buyer
      const res = await axios.get(`${API}/orders`, {
        params: {
          buyer_id: credit.buyer_id,
          payment_status: 'pending'
        }
      });
      setBuyerOrders(res.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setBuyerOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleMarkAsPaid = async (orderId: string) => {
    try {
      await axios.post(`${API}/billing/mark-paid/${orderId}`);
      toast.success('Ordine segnato come pagato');
      
      // Aggiorna i dati
      fetchData();
      if (selectedBuyer) {
        handleViewBuyerDetails(selectedBuyer);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Errore nel segnare come pagato');
    }
  };

  const filteredCredits = credits.filter(c => 
    c.buyer_company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.buyer_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Solo per seller
  if (user?.role !== 'seller') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Questa pagina è riservata ai seller.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wallet className="h-7 w-7" />
          Gestione Crediti
        </h1>
        <p className="text-muted-foreground mt-1">
          Monitora i crediti dei tuoi clienti e gli ordini in sospeso
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Clienti con Credito</p>
                <p className="text-2xl font-bold">{summary?.active_credits_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Totale in Sospeso</p>
                <p className="text-2xl font-bold">{formatCurrency(summary?.total_outstanding || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Limite Totale Concesso</p>
                <p className="text-2xl font-bold">{formatCurrency(summary?.total_credit_limit || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Credits Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Clienti con Credito Attivo</CardTitle>
              <CardDescription>Clicca su un cliente per vedere i dettagli</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredCredits.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun cliente con credito attivo</p>
              <p className="text-sm mt-2">
                Vai nella pagina Clienti per configurare il credito per i tuoi buyer fidati.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-center">Tipo</TableHead>
                  <TableHead className="text-right">Limite</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                  <TableHead className="text-right">Disponibile</TableHead>
                  <TableHead className="text-center">Stato</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCredits.map((credit) => {
                  const available = credit.credit_limit - credit.current_balance;
                  const usagePercent = credit.credit_limit > 0 
                    ? (credit.current_balance / credit.credit_limit * 100) 
                    : 0;
                  const isRevolving = credit.credit_type === 'revolving';
                  
                  return (
                    <TableRow 
                      key={credit.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewBuyerDetails(credit)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-muted rounded-lg">
                            <Building2 className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="font-medium">{credit.buyer_company || 'N/A'}</p>
                            <p className="text-sm text-muted-foreground">{credit.buyer_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {isRevolving ? (
                          <Badge variant="outline" className="gap-1">
                            <RefreshCw className="h-3 w-3" />
                            Rotativo
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Semplice</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(credit.credit_limit)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={credit.current_balance > 0 ? 'text-amber-600 font-medium' : ''}>
                          {formatCurrency(credit.current_balance)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={available <= 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                          {formatCurrency(available)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {usagePercent >= 100 ? (
                          <Badge variant="destructive">Limite Raggiunto</Badge>
                        ) : usagePercent >= 80 ? (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            Quasi al limite
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            OK
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Buyer Details Dialog */}
      <Dialog open={!!selectedBuyer} onOpenChange={() => setSelectedBuyer(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {selectedBuyer?.buyer_company}
            </DialogTitle>
            <DialogDescription>
              {selectedBuyer?.buyer_email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Credit Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-muted/30 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">Limite</p>
                <p className="text-xl font-bold">{formatCurrency(selectedBuyer?.credit_limit || 0)}</p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">In Sospeso</p>
                <p className="text-xl font-bold text-amber-600">
                  {formatCurrency(selectedBuyer?.current_balance || 0)}
                </p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <p className="text-sm text-muted-foreground">Disponibile</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency((selectedBuyer?.credit_limit || 0) - (selectedBuyer?.current_balance || 0))}
                </p>
              </div>
            </div>

            {/* Unpaid Orders */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Ordini da Pagare
              </h4>
              
              {loadingOrders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : buyerOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>Nessun ordine in sospeso</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {buyerOrders.map((order) => (
                    <div 
                      key={order.id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">Ordine #{order.order_number || order.id.slice(0,8)}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold">{formatCurrency(order.total || 0)}</span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsPaid(order.id);
                          }}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Pagato
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedBuyer?.notes && (
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Note:</p>
                <p className="text-sm">{selectedBuyer.notes}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SellerCreditsPage;
